import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { db } from '~/lib/db.server';
import { motion, AnimatePresence, useMotionValue, useTransform, useSpring, useScroll } from 'framer-motion';
import type { advisors } from '@prisma/client';
import { useState, useEffect, useRef } from 'react';
import { serializePrismaData } from '~/lib/utils';

export const meta: MetaFunction = () => {
  return [
    { title: '전문위원 소개 - 법무법인 휘상' },
    { name: 'description', content: '법무법인 휘상의 전문위원을 소개합니다.' }
  ];
};

export const loader: LoaderFunction = async () => {
  const advisors = await db.advisors.findMany({
    where: {
      deleted_at: null,
    },
    orderBy: {
      order: 'asc',
    },
  });

  // BigInt 및 Date 등의 값을 안전하게 직렬화
  const serializedAdvisors = serializePrismaData(advisors);

  return json({ advisors: serializedAdvisors });
};

export default function SpecialistsPage() {
  const { advisors } = useLoaderData<typeof loader>();
  const [selectedAdvisor, setSelectedAdvisor] = useState<advisors | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  // 모든 전문 분야를 추출하여 중복 제거
  const allSpecialties = [...new Set(advisors.flatMap((advisor: advisors) => advisor.specialties))] as string[];

  // 필터링된 전문위원 목록
  const filteredAdvisors = activeFilter === 'all' 
    ? advisors 
    : advisors.filter((advisor: advisors) => advisor.specialties.includes(activeFilter));

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 50 },
    show: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20,
        duration: 0.8
      }
    }
  };

  const subtitleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 50,
        damping: 20,
        delay: 0.2,
        duration: 0.8
      }
    }
  };

  const filterVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20,
        delay: 0.4
      }
    }
  };

  return (
    <div className="pt-16 bg-gradient-to-b from-gray-50 to-white min-h-screen overflow-hidden">
      {/* 스크롤 진행 표시기 */}
      <motion.div 
        className="fixed top-0 left-0 right-0 h-1 bg-[#b98e6a] z-50 origin-left"
        style={{ scaleX }}
      />
      
      {/* 헤더 섹션 */}
      <div className="relative bg-gradient-to-r from-[#2c1810] to-[#4a2c1a] py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/images/lawyers.jpg')] opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-[#2c1810]/60"></div>
        <div className="absolute -bottom-10 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <motion.h1 
            initial="hidden"
            animate="visible"
            variants={titleVariants}
            className="text-5xl md:text-7xl font-bold text-white mb-8 tracking-tight"
          >
            전문위원 소개
          </motion.h1>
          <motion.p 
            initial="hidden"
            animate="visible"
            variants={subtitleVariants}
            className="text-xl text-white/90 max-w-2xl font-light"
          >
            법무법인 휘상의 전문위원들이 다양한 분야에서 전문적인 법률자문을 제공합니다
          </motion.p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* 필터 섹션 */}
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={filterVariants}
          className="mb-12 overflow-x-auto pb-4"
        >
          <div className="flex space-x-2 min-w-max">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 shadow-sm ${
                activeFilter === 'all'
                  ? 'bg-[#b98e6a] text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              전체
            </button>
            {allSpecialties.map((specialty: string, index: number) => (
              <button
                key={index}
                onClick={() => setActiveFilter(specialty)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 shadow-sm ${
                  activeFilter === specialty
                    ? 'bg-[#b98e6a] text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {specialty}
              </button>
            ))}
          </div>
        </motion.div>
        
        {/* 전문위원 카드 그리드 */}
        <motion.div 
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
        >
          {filteredAdvisors.map((advisor: advisors) => (
            <AdvisorCard 
              key={advisor.id.toString()} 
              advisor={advisor} 
              setSelectedAdvisor={setSelectedAdvisor}
              variants={item}
            />
          ))}
        </motion.div>

        {filteredAdvisors.length === 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <p className="text-gray-500 text-lg">현재 등록된 전문위원이 없습니다.</p>
          </motion.div>
        )}
      </div>

      {/* 전문위원 상세 모달 */}
      <AnimatePresence>
        {selectedAdvisor && (
          <AdvisorDetailModal 
            advisor={selectedAdvisor} 
            onClose={() => setSelectedAdvisor(null)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// 전문위원 카드 컴포넌트
const AdvisorCard = ({ advisor, setSelectedAdvisor, variants }: { 
  advisor: advisors, 
  setSelectedAdvisor: (advisor: advisors) => void,
  variants: any
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  
  return (
    <motion.div 
      ref={cardRef}
      variants={variants}
      whileHover={{ 
        y: -10,
        transition: { type: "spring", stiffness: 300, damping: 20 }
      }}
      className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer will-change-transform h-full flex flex-col"
      onClick={() => setSelectedAdvisor(advisor)}
      layout
    >
      <div className="aspect-square w-full overflow-hidden relative">
        {advisor.image_url ? (
          <motion.div className="w-full h-full overflow-hidden">
            <motion.img
              src={advisor.image_url}
              alt={`${advisor.name} 프로필`}
              className="w-full h-full object-cover"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.6, ease: [0.43, 0.13, 0.23, 0.96] }}
            />
          </motion.div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <motion.svg 
              className="h-24 w-24 text-gray-300" 
              fill="currentColor" 
              viewBox="0 0 24 24"
              initial={{ scale: 0.8, opacity: 0.5 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: 2 
              }}
            >
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </motion.svg>
          </div>
        )}
        {!advisor.is_active && (
          <motion.div 
            className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 text-xs rounded-md font-medium z-10"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            휴직중
          </motion.div>
        )}
        
        <motion.div 
          className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
        />
      </div>
      
      <div className="px-6 py-5 relative text-center flex flex-col flex-grow">
        <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-[#b98e6a] to-[#e6a87c] transform -translate-y-1 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
        
        <div className="mb-auto">
          <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-[#b98e6a] transition-colors duration-300">
            {advisor.name}
          </h3>
          <p className="text-sm text-gray-600 font-medium mb-3">
            {advisor.position || '전문위원'}
          </p>
          
          {/* 역할 표시 */}
          {advisor.role && (
            <p className="text-sm text-gray-700 mb-3 bg-gray-100 p-1.5 rounded-md inline-block">
              {advisor.role}
            </p>
          )}
          
          {advisor.specialties.length > 0 && (
            <div className="mt-3 flex flex-wrap justify-center gap-1 mb-4">
              {advisor.specialties.slice(0, 3).map((specialty: string, index: number) => (
                <motion.span 
                  key={index} 
                  className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md"
                  whileHover={{ 
                    backgroundColor: '#f3e8de',
                    color: '#b98e6a',
                    scale: 1.05
                  }}
                  transition={{ duration: 0.2 }}
                >
                  {specialty}
                </motion.span>
              ))}
              {advisor.specialties.length > 3 && (
                <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-md">
                  +{advisor.specialties.length - 3}
                </span>
              )}
            </div>
          )}
          
          <div className="h-[72px]"> {/* 설명 영역의 높이 고정 */}
            {advisor.description ? (
              <p className="text-sm text-gray-600 line-clamp-3">{advisor.description}</p>
            ) : (
              advisor.collaboration ? (
                <p className="text-sm text-gray-600 line-clamp-3">협력 방식: {advisor.collaboration}</p>
              ) : (
                <p className="text-sm text-gray-400 italic">소개 내용이 없습니다</p>
              )
            )}
          </div>
        </div>
        
        <motion.div 
          className="mt-4 text-[#b98e6a] text-sm font-medium flex items-center justify-center opacity-70 group-hover:opacity-100 transition-all duration-300"
          whileHover={{ scale: 1.05 }}
        >
          <span>자세히 보기</span>
          <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </motion.div>
      </div>
    </motion.div>
  );
};

// 전문위원 상세 모달 컴포넌트
const AdvisorDetailModal = ({ advisor, onClose }: { advisor: advisors, onClose: () => void }) => {
  // 모달이 열릴 때 스크롤을 방지
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const modalVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.1 
      }
    },
    exit: { 
      opacity: 0,
      transition: { 
        when: "afterChildren",
        staggerChildren: 0.05,
        staggerDirection: -1 
      }
    }
  };

  const contentVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "spring", 
        stiffness: 200, 
        damping: 20 
      }
    },
    exit: { 
      y: -50, 
      opacity: 0,
      transition: { 
        type: "spring", 
        stiffness: 200, 
        damping: 20 
      }
    }
  };

  return (
    <motion.div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={modalVariants}
      onClick={onClose}
    >
      <motion.div 
        className="bg-white rounded-2xl overflow-hidden max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        variants={contentVariants}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col text-center">
          {/* 상단 이미지 (오버레이 효과 적용) */}
          <div className="relative overflow-hidden">
            {advisor.image_url ? (
              <div className="relative">
                <motion.div 
                  className="w-full"
                  initial={{ scale: 1.1, opacity: 0.8 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6 }}
                >
                  <img
                    src={advisor.image_url}
                    alt={`${advisor.name} 프로필`}
                    className="w-full object-cover object-center"
                  />
                  {/* 그라데이션 오버레이 - 위쪽은 투명, 아래쪽으로 갈수록 불투명한 흰색 */}
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/80 to-transparent h-full"></div>
                </motion.div>
                
                {/* 이름과 직책 (사진 아래쪽 중앙에 위치) */}
                <div className="absolute bottom-6 left-0 right-0 text-center z-10">
                  <motion.h2 
                    className="text-3xl font-bold text-gray-900 mb-2 drop-shadow-sm"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    {advisor.name}
                  </motion.h2>
                  <motion.p 
                    className="text-lg text-gray-700 font-medium drop-shadow-sm"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    {advisor.position || '전문위원'}
                  </motion.p>
                </div>
              </div>
            ) : (
              <div className="w-full pt-16 pb-24 flex flex-col items-center justify-center bg-gradient-to-b from-gray-100 to-white">
                <motion.svg 
                  className="h-32 w-32 text-gray-300 mb-4" 
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                  initial={{ scale: 0.8, opacity: 0.5 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ 
                    repeat: Infinity, 
                    repeatType: "reverse", 
                    duration: 2 
                  }}
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </motion.svg>
                <motion.h2 
                  className="text-3xl font-bold text-gray-800 mb-2"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {advisor.name}
                </motion.h2>
                <motion.p 
                  className="text-lg text-gray-600"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  {advisor.position || '전문위원'}
                </motion.p>
              </div>
            )}
            
            <motion.button 
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 transition-colors bg-white/70 backdrop-blur-sm p-2 rounded-full z-20 shadow-md"
              onClick={onClose}
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.1, backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </motion.button>
          </div>
          
          {/* 내용 섹션 */}
          <div className="px-8 pb-8 pt-4 bg-white">
            {/* 역할 */}
            {advisor.role && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mb-10"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4 inline-flex items-center justify-center">
                  <span className="w-5 h-5 bg-[#b98e6a]/20 rounded-full flex items-center justify-center mr-2">
                    <span className="w-2.5 h-2.5 bg-[#b98e6a] rounded-full"></span>
                  </span>
                  역할
                </h3>
                <p className="text-gray-700 whitespace-pre-line bg-gray-50 p-4 rounded-lg text-center">
                  {advisor.role}
                </p>
              </motion.div>
            )}

            {/* 협력 방식 */}
            {advisor.collaboration && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mb-10"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4 inline-flex items-center justify-center">
                  <span className="w-5 h-5 bg-[#b98e6a]/20 rounded-full flex items-center justify-center mr-2">
                    <span className="w-2.5 h-2.5 bg-[#b98e6a] rounded-full"></span>
                  </span>
                  법무법인과의 협력 방식
                </h3>
                <p className="text-gray-700 whitespace-pre-line bg-gray-50 p-4 rounded-lg text-center">
                  {advisor.collaboration}
                </p>
              </motion.div>
            )}

            {/* 소개 */}
            {advisor.description && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mb-10"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4 inline-flex items-center justify-center">
                  <span className="w-5 h-5 bg-[#b98e6a]/20 rounded-full flex items-center justify-center mr-2">
                    <span className="w-2.5 h-2.5 bg-[#b98e6a] rounded-full"></span>
                  </span>
                  소개
                </h3>
                <p className="text-gray-700 whitespace-pre-line bg-gray-50 p-4 rounded-lg text-center">
                  {advisor.description}
                </p>
              </motion.div>
            )}

            {/* 전문 분야 */}
            <motion.div 
              className="mb-10"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 inline-flex items-center justify-center">
                <span className="w-5 h-5 bg-[#b98e6a]/20 rounded-full flex items-center justify-center mr-2">
                  <span className="w-2.5 h-2.5 bg-[#b98e6a] rounded-full"></span>
                </span>
                전문 분야
              </h3>
              <div className="flex flex-wrap justify-center gap-2">
                {advisor.specialties.map((specialty: string, index: number) => (
                  <motion.span 
                    key={index} 
                    className="inline-block px-3 py-1.5 text-sm bg-[#b98e6a]/10 text-[#b98e6a] rounded-xl"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.7 + index * 0.05 }}
                    whileHover={{ scale: 1.05, backgroundColor: 'rgba(185, 142, 106, 0.2)' }}
                  >
                    {specialty}
                  </motion.span>
                ))}
              </div>
            </motion.div>

            {/* 학력 */}
            {advisor.education && advisor.education.length > 0 && (
              <motion.div 
                className="mb-10"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4 inline-flex items-center justify-center">
                  <span className="w-5 h-5 bg-[#b98e6a]/20 rounded-full flex items-center justify-center mr-2">
                    <span className="w-2.5 h-2.5 bg-[#b98e6a] rounded-full"></span>
                  </span>
                  학력
                </h3>
                <ul className="space-y-3">
                  {advisor.education.map((edu: string, index: number) => (
                    <motion.li 
                      key={index} 
                      className="text-gray-700 block bg-gray-50 p-3 rounded-lg text-center"
                      initial={{ y: 10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.8 + index * 0.05 }}
                    >
                      {edu}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* 경력 */}
            {advisor.career && advisor.career.length > 0 && (
              <motion.div 
                className="mb-10"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4 inline-flex items-center justify-center">
                  <span className="w-5 h-5 bg-[#b98e6a]/20 rounded-full flex items-center justify-center mr-2">
                    <span className="w-2.5 h-2.5 bg-[#b98e6a] rounded-full"></span>
                  </span>
                  경력
                </h3>
                <ul className="space-y-3">
                  {advisor.career.map((career: string, index: number) => (
                    <motion.li 
                      key={index} 
                      className="text-gray-700 block bg-gray-50 p-3 rounded-lg text-center"
                      initial={{ y: 10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.9 + index * 0.05 }}
                    >
                      {career}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* 연락처 */}
            {advisor.email && (
              <motion.div 
                className="mt-12 flex items-center justify-center text-gray-700 bg-[#b98e6a]/5 p-4 rounded-lg"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1 }}
                whileHover={{ backgroundColor: 'rgba(185, 142, 106, 0.1)', scale: 1.02 }}
              >
                <div className="bg-[#b98e6a]/10 p-2 rounded-full mr-3">
                  <svg className="h-5 w-5 text-[#b98e6a]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="font-medium">{advisor.email}</span>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
